import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global error interceptor
let isRedirecting = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      // 401 Unauthorized — auto logout & redirect
      if (status === 401 && !isRedirecting) {
        isRedirecting = true;
        localStorage.removeItem('auth_token');
        window.location.href = '/?login=true';
        return Promise.reject(error);
      }

      // 500 Server Error
      if (status >= 500) {
        import('sonner').then(({ toast }) => {
          toast.error('Terjadi kesalahan server. Silakan coba lagi.');
        });
      }
    } else if (error.request) {
      // Network error
      import('sonner').then(({ toast }) => {
        toast.error('Tidak dapat terhubung ke server. Periksa koneksi internet Anda.');
      });
    }

    return Promise.reject(error);
  }
);

// Use native fetch for FormData uploads — axios may interfere with
// multipart/form-data boundary even without a default Content-Type.
const postFormData = async (path, formData) => {
  const token = localStorage.getItem('auth_token');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // Do NOT set Content-Type — browser will set multipart/form-data with boundary automatically
  const res = await fetch(`${API}${path}`, {
    method: 'POST',
    headers,
    body: formData,
  });
  const json = await res.json();
  if (!res.ok) {
    const err = new Error(json.detail || 'Upload failed');
    err.response = { data: json, status: res.status };
    throw err;
  }
  return json;
};

// Auth API
export const authAPI = {
  register: async (data) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  login: async (data) => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Courses API (Modul)
export const coursesAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/courses', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/courses/${id}`);
    return response.data;
  },

  enroll: async (courseId) => {
    const response = await api.post(`/courses/${courseId}/enroll`);
    return response.data;
  },
};

// User API
export const userAPI = {
  getEnrollments: async () => {
    const response = await api.get('/users/enrollments');
    return response.data;
  },

  updateProgress: async (courseId, lessonId, completed = true) => {
    const response = await api.post(`/users/progress/${courseId}`, {
      lesson_id: lessonId,
      completed
    });
    return response.data;
  },

  getProgress: async (courseId) => {
    const response = await api.get(`/users/progress/${courseId}`);
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/users/profile', data);
    return response.data;
  },

  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return postFormData('/users/avatar', formData);
  },
};

// Certificates API
export const certificatesAPI = {
  generate: async (courseId) => {
    const response = await api.post(`/certificates/${courseId}`);
    return response.data;
  },

  getAll: async () => {
    const response = await api.get('/certificates');
    return response.data;
  },

  getById: async (certificateId) => {
    const response = await api.get(`/certificates/${certificateId}`);
    return response.data;
  },
};

// Payment API
export const paymentAPI = {
  create: async (data) => {
    const response = await api.post('/payments', data);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/payments/${id}`);
    return response.data;
  },

  getAll: async (status) => {
    const params = status ? { status_filter: status } : {};
    const response = await api.get('/payments', { params });
    return response.data;
  },

  confirm: async (paymentId) => {
    const response = await api.post(`/payments/${paymentId}/confirm`);
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  getAnalytics: async () => {
    const response = await api.get('/admin/analytics');
    return response.data;
  },

  getUsers: async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  updateUserRole: async (userId, role) => {
    const response = await api.put(`/admin/users/${userId}/role`, null, { params: { role } });
    return response.data;
  },

  createCourse: async (data) => {
    const response = await api.post('/admin/courses', data);
    return response.data;
  },

  updateCourse: async (courseId, data) => {
    const response = await api.put(`/admin/courses/${courseId}`, data);
    return response.data;
  },

  deleteCourse: async (courseId) => {
    const response = await api.delete(`/admin/courses/${courseId}`);
    return response.data;
  },

  getPayments: async (params = {}) => {
    const response = await api.get('/admin/payments', { params });
    return response.data;
  },

  confirmPayment: async (paymentId) => {
    const response = await api.post(`/admin/payments/${paymentId}/confirm`);
    return response.data;
  },

  getEnrollments: async (params = {}) => {
    const response = await api.get('/admin/enrollments', { params });
    return response.data;
  },

  getDonations: async (params = {}) => {
    const response = await api.get('/admin/donations', { params });
    return response.data;
  },

  confirmDonation: async (donationId) => {
    const response = await api.post(`/admin/donations/${donationId}/confirm`);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  deleteDonation: async (donationId) => {
    const response = await api.delete(`/admin/donations/${donationId}`);
    return response.data;
  },

  deleteEnrollment: async (enrollmentId) => {
    const response = await api.delete(`/admin/enrollments/${enrollmentId}`);
    return response.data;
  },
};

// Lessons API
export const lessonsAPI = {
  getByCourse: async (courseId) => {
    const response = await api.get(`/lessons/course/${courseId}`);
    return response.data;
  },

  getById: async (lessonId) => {
    const response = await api.get(`/lessons/${lessonId}`);
    return response.data;
  },

  complete: async (lessonId) => {
    const response = await api.post(`/lessons/${lessonId}/complete`);
    return response.data;
  },

  create: async (courseId, data) => {
    const response = await api.post(`/lessons/course/${courseId}`, data);
    return response.data;
  },

  update: async (lessonId, data) => {
    const response = await api.put(`/lessons/${lessonId}`, data);
    return response.data;
  },

  delete: async (lessonId) => {
    const response = await api.delete(`/lessons/${lessonId}`);
    return response.data;
  },
};

// Moderator API (content management — admin & moderator only)
export const moderatorAPI = {
  getStats: async () => {
    const response = await api.get('/moderator/stats');
    return response.data;
  },

  getCourses: async (params = {}) => {
    const response = await api.get('/moderator/courses', { params });
    return response.data;
  },

  createCourse: async (data) => {
    const response = await api.post('/moderator/courses', data);
    return response.data;
  },

  updateCourse: async (courseId, data) => {
    const response = await api.put(`/moderator/courses/${courseId}`, data);
    return response.data;
  },

  deleteCourse: async (courseId) => {
    const response = await api.delete(`/moderator/courses/${courseId}`);
    return response.data;
  },

  getStudents: async (params = {}) => {
    const response = await api.get('/moderator/students', { params });
    return response.data;
  },
};

// Keep backward compat alias
export const instructorAPI = moderatorAPI;

// Uploads API
export const uploadsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/uploads', { params });
    return response.data;
  },

  getById: async (fileId) => {
    const response = await api.get(`/uploads/file/${fileId}`);
    return response.data;
  },

  delete: async (fileId) => {
    const response = await api.delete(`/uploads/file/${fileId}`);
    return response.data;
  },

  upload: async (file, title = '') => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    return postFormData('/uploads', formData);
  },
};

// === Learning Paths API (Jalur Belajar) ===
export const pathsAPI = {
  getAll: async () => {
    const response = await api.get('/paths');
    return response.data;
  },

  getById: async (pathId) => {
    const response = await api.get(`/paths/${pathId}`);
    return response.data;
  },

  getModules: async (pathId) => {
    const response = await api.get(`/paths/${pathId}/modules`);
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/paths', data);
    return response.data;
  },

  update: async (pathId, data) => {
    const response = await api.put(`/paths/${pathId}`, data);
    return response.data;
  },

  delete: async (pathId) => {
    const response = await api.delete(`/paths/${pathId}`);
    return response.data;
  },
};

// === NEW: Reflections API (Jurnal Refleksi) ===
export const reflectionsAPI = {
  create: async (data) => {
    const response = await api.post('/reflections', data);
    return response.data;
  },

  getAll: async (params = {}) => {
    const response = await api.get('/reflections', { params });
    return response.data;
  },

  getSummary: async () => {
    const response = await api.get('/reflections/summary');
    return response.data;
  },

  getByModule: async (moduleId) => {
    const response = await api.get(`/reflections/module/${moduleId}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/reflections/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/reflections/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/reflections/${id}`);
    return response.data;
  },
};

// === Donations API (Manual Bank Transfer + Admin Confirmation) ===
export const donationsAPI = {
  create: async (data) => {
    const response = await api.post('/donations', data);
    return response.data;
  },

  getHistory: async (params = {}) => {
    const response = await api.get('/donations', { params });
    return response.data;
  },

  uploadProof: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return postFormData('/donations/upload-proof', formData);
  },
};

// === UPDATED: Discussions API (Dialog — no likes, no resolve) ===
export const discussionsAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/discussions', { params });
    return response.data;
  },

  getById: async (discussionId) => {
    const response = await api.get(`/discussions/${discussionId}`);
    return response.data;
  },

  create: async (data) => {
    if (data.image) {
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('content', data.content);
      if (data.room) formData.append('room', data.room);
      if (data.module_id) formData.append('module_id', data.module_id);
      if (data.target_role) formData.append('target_role', data.target_role);
      formData.append('image', data.image);
      return postFormData('/discussions', formData);
    }
    const response = await api.post('/discussions', data);
    return response.data;
  },

  update: async (discussionId, data) => {
    const response = await api.put(`/discussions/${discussionId}`, data);
    return response.data;
  },

  delete: async (discussionId) => {
    const response = await api.delete(`/discussions/${discussionId}`);
    return response.data;
  },

  // Reply endpoints
  createReply: async (discussionId, data) => {
    if (data.image) {
      const formData = new FormData();
      formData.append('content', data.content);
      formData.append('image', data.image);
      return postFormData(`/discussions/${discussionId}/replies`, formData);
    }
    const response = await api.post(`/discussions/${discussionId}/replies`, data);
    return response.data;
  },

  updateReply: async (discussionId, replyId, data) => {
    const response = await api.put(`/discussions/${discussionId}/replies/${replyId}`, data);
    return response.data;
  },

  deleteReply: async (discussionId, replyId) => {
    const response = await api.delete(`/discussions/${discussionId}/replies/${replyId}`);
    return response.data;
  },

  getRooms: async () => {
    const response = await api.get('/discussions/rooms/available');
    return response.data;
  },
};

// === Static Pages API (Wacana & Nilai) ===
export const pagesAPI = {
  getAll: async () => {
    const response = await api.get('/pages');
    return response.data;
  },

  getBySlug: async (slug) => {
    const response = await api.get(`/pages/${slug}`);
    return response.data;
  },

  // Admin CRUD
  adminGetAll: async () => {
    const response = await api.get('/admin/pages');
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/pages', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/pages/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/pages/${id}`);
    return response.data;
  },

  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return postFormData('/admin/pages/upload-image', formData);
  },
};

// === Articles API (Blog/Artikel) ===
export const articlesAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/articles', { params });
    return response.data;
  },

  getBySlug: async (slug) => {
    const response = await api.get(`/articles/${slug}`);
    return response.data;
  },

  // Admin CRUD
  adminGetAll: async () => {
    const response = await api.get('/admin/articles');
    return response.data;
  },

  create: async (data) => {
    const response = await api.post('/admin/articles', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/articles/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/articles/${id}`);
    return response.data;
  },

  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return postFormData('/admin/articles/upload-image', formData);
  },
};

// === Settings API ===
export const settingsAPI = {
  getPublic: async () => {
    const response = await api.get('/settings/public');
    return response.data;
  },

  // Admin
  getAll: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  update: async (key, value) => {
    const response = await api.put(`/admin/settings/${key}`, { value });
    return response.data;
  },
};

export default api;